import React from 'react';
import VideoThumbnail from '../ideas/attachment-library/VideoThumbnail';

interface MediaThumbImageProps {
  /** Preferred small webp derivative (backend `/media/thumb/*`). May 404. */
  thumbUrl?: string;
  /** Full-resolution still-image original — fallback when the derivative 404s. */
  originalUrl?: string;
  /**
   * Playable video URL for video-only scenes. Used as the terminal visual (a
   * first-frame poster) when no still-image derivative/original decodes — never
   * fed to <img> directly, since <img src="…mp4"> can only ever paint alt text.
   */
  videoUrl?: string;
  alt: string;
  /** Wrapper class (sizing/rounding) for the video / placeholder box. */
  className?: string;
  /** Class for the <img> element itself (e.g. `object-cover`). */
  imgClassName?: string;
  /**
   * Flip the still-image ladder to original-first. Small storyboard squares keep
   * the default (cheap webp derivative first); a large detail preview passes
   * this so it shows the full-resolution asset up front and only degrades to the
   * derivative on error.
   */
  preferOriginal?: boolean;
}

type Attempt =
  | { kind: 'img'; src: string }
  | { kind: 'video'; src: string }
  | { kind: 'placeholder' };

const buildAttempts = (
  thumbUrl?: string,
  originalUrl?: string,
  videoUrl?: string,
  preferOriginal?: boolean
): Attempt[] => {
  const attempts: Attempt[] = [];
  const imgSources = preferOriginal ? [originalUrl, thumbUrl] : [thumbUrl, originalUrl];
  const seen = new Set<string>();
  imgSources.forEach((src) => {
    if (src && !seen.has(src)) {
      seen.add(src);
      attempts.push({ kind: 'img', src });
    }
  });
  if (videoUrl) attempts.push({ kind: 'video', src: videoUrl });
  attempts.push({ kind: 'placeholder' });
  return attempts;
};

const Placeholder: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={`flex items-center justify-center bg-zinc-900 text-zinc-600 ${className ?? ''}`}
    aria-hidden="true"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-5 h-5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
      />
    </svg>
  </div>
);

/**
 * Renders a storyboard/grid thumbnail with a resilient fallback ladder:
 * webp derivative → full-resolution original → video first-frame → neutral
 * placeholder. Advancing happens on the <img> `error` event, so a broken image
 * never falls through to Firefox painting the `alt` prompt text into the box.
 */
const MediaThumbImage: React.FC<MediaThumbImageProps> = ({
  thumbUrl,
  originalUrl,
  videoUrl,
  alt,
  className,
  imgClassName,
  preferOriginal,
}) => {
  const attempts = React.useMemo(
    () => buildAttempts(thumbUrl, originalUrl, videoUrl, preferOriginal),
    [thumbUrl, originalUrl, videoUrl, preferOriginal]
  );
  const [stage, setStage] = React.useState(0);
  React.useEffect(() => {
    setStage(0);
  }, [attempts]);

  const current = attempts[Math.min(stage, attempts.length - 1)];

  if (current.kind === 'video') {
    return (
      <div className={className}>
        <VideoThumbnail src={current.src} />
      </div>
    );
  }
  if (current.kind === 'placeholder') {
    return <Placeholder className={className} />;
  }
  return (
    <img
      src={current.src}
      alt={alt}
      className={imgClassName}
      loading="lazy"
      decoding="async"
      onError={() => setStage((s) => s + 1)}
    />
  );
};

export default MediaThumbImage;
